import { PopupType } from '@pp/core/src/components/popup/types';
import { CrossOriginCommunication } from '@pp/core/src/controllers/crossOriginCommunication/CrossOriginCommunicationController';
import { OutgoingMessage } from '@pp/core/src/controllers/crossOriginCommunication/types';
import { getAcceptedBets } from '@pp/core/src/controllers/gameCommunication/betUtilities/getAcceptedBets';
import { BaseCoreGameCommunicationProcessor } from '@pp/core/src/controllers/gameCommunication/processors/BaseCoreGameCommunicationProcessor';
import { IGameCommunicationProcessor, MessageName } from '@pp/core/src/controllers/gameCommunication/types';
import { BetStateBitMask, betStateBitMask, updateBetStateBitMask } from '@pp/core/src/logger/BetStateBitMask';
import { Logger } from '@pp/core/src/logger/Logger';
import { RoundIntegrityWatcher, RoundIntegrityWatcherPhases } from '@pp/core/src/logger/RoundIntegrityWatcher';
import { FeatureService } from '@pp/core/src/services/features/FeatureService';
import { AutoplayStatus } from '@pp/core/src/store/slices/autoplay/types';
import { betActions } from '@pp/core/src/store/slices/bets/slice';
import { BetType } from '@pp/core/src/store/slices/bets/types';
import { gameStateActions } from '@pp/core/src/store/slices/game/slice';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { popupActions } from '@pp/core/src/store/slices/popup/slice';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';
import { roundNumber } from '@pp/core/src/utils/number';

import { CrashPopupType } from '../../../components/popup/CrashAutoplayModifiedConnected';
import { crashAutoplayActions } from '../../../store/slices/crashAutoplay/slice';

declare global {
  interface Window {
    betStateBitMask?: number;
  }
}

export interface ComputedBet {
  betId: number;
  value: number;
}

export class GameHFCommunicationBetsClosedProcessor
  extends BaseCoreGameCommunicationProcessor
  implements IGameCommunicationProcessor
{
  public messageName = MessageName.BetsClosed;
  public delay = 0;

  process(): void {
    RoundIntegrityWatcher.setPhase(RoundIntegrityWatcherPhases.BetsClosed);
    CrossOriginCommunication.dispatch(OutgoingMessage.BetsClosed);

    this.logData();

    const tableId = this.store.getState().config.session?.tableId ?? '';

    this.store.dispatch(gameStateActions.setWinners([]));
    this.store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.BetsClosed));
    this.store.dispatch(betActions.saveLastBets({ tableId }));

    this.placeBets();

    const { crashAutoplay, bets, config } = this.store.getState();
    Object.keys(bets?.betList || {})
      .map(Number)
      .forEach((bet) => {
        const betId = bets.betList ? bets.betList[bet] : 1;
        const isAutoplayV2 = FeatureService.getFeature("isAutoplayV2") ?? false;
        if (crashAutoplay.isInProgress[betId]) {
          if (
            crashAutoplay.isInProgress[betId] ||
            (crashAutoplay.autoplayStatus === AutoplayStatus.Paused && !!bets.acceptedBets.length)
          ) {
            const currentPopup = this.store.getState().popup.currentPopup?.popupName;
            if (
              (currentPopup === 'CrashAutoplayModified_1' && betId == 1) ||
              (currentPopup === 'CrashAutoplayModified_2' && betId == 2)
            ) {
              this.store.dispatch(
                popupActions.dismissPopup(
                  betId == 1 ? CrashPopupType.CrashAutoplayModified_1 : CrashPopupType.CrashAutoplayModified_2
                )
              );
              this.store.dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
            }
          }
          if (
            crashAutoplay.roundsLeft[betId] <= 0 &&
            !crashAutoplay.isEnabled[betId] &&
            !config.tableConfig?.isInfiniteAutoPlayEnabled
          ) {
            this.store.dispatch(toastActions.createToast('AUTOPLAY_COMPLETED'));
            this.store.dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
            if (isAutoplayV2) {
              this.store.dispatch(crashAutoplayActions.openModal(betId));
            }
            Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, 'Completed', false);
          }
        }
        this.store.dispatch(crashAutoplayActions.setIsCancelled({ bestSpot: betId, cancelled: false }));
        this.store.dispatch(crashAutoplayActions.setIsThisRoundOnly({ bestSpot: betId, thisRoundonly: false }));
      });
  }

  private logData(): void {
    const { bets, popup, settings } = this.store.getState();

    if (popup.currentPopup !== null) {
      if (popup.currentPopup.popupName === PopupType.Inactivity) {
        updateBetStateBitMask(BetStateBitMask.Inactivity);
      } else if (popup.currentPopup.popupName !== PopupType.InsufficientFunds) {
        updateBetStateBitMask(BetStateBitMask.OtherBlockingPopup);
      }
    }

    if (settings.volume.master.value > 0) {
      if (settings.volume.channels['studio'] && settings.volume.channels['studio'].value > 0) {
        updateBetStateBitMask(BetStateBitMask.VideoSoundOn);
      }
      if (settings.volume.channels['game'] && settings.volume.channels['game'].value > 0) {
        updateBetStateBitMask(BetStateBitMask.GameSoundOn);
      }
    }
    if (settings.stream.isEnabled) {
      updateBetStateBitMask(BetStateBitMask.VideoOn);
    }
    Logger.logEvent('click', 'roundClosed', Date.now(), 1, '' + (bets.betStack.length > 0), false, betStateBitMask);
    window.betStateBitMask = 0;
  }

  private computeBets(): ComputedBet[] {
    const { crashAutoplay, bets } = this.store.getState();
    const bet = bets.betStack[bets.betStack.length - 1];
    const computedBets: { betId: number; value: number }[] = [];
    Object.keys(bets?.betList || {})
      .map(Number)
      .forEach((sBet) => {
        const betId = bets.betList ? bets.betList[sBet] : 1;
        let autoplayBets;

        if (crashAutoplay.isInProgress[betId]) {
          autoplayBets = crashAutoplay.savedBet as BetType;
        }

        if (autoplayBets) {
          const singleBetboarBets = bet?.betSpots[betId] && bet?.betSpots[betId].value;

          if (singleBetboarBets) {
            this.store.dispatch(crashAutoplayActions.decRoundCount({ bestSpot: betId, count: 1 }));
          }
        }
        if (bet) {
          const betValue = bet?.betSpots[betId]?.value;
          if (betValue) {
            computedBets.push({ betId, value: betValue });
          }
        }
      });
    return computedBets;
  }

  protected areBetsModified(): boolean {
    return false;
  }
  private updateAcceptedBets(computedBets: ComputedBet[]): void {
    this.store.dispatch(betActions.setAcceptedBets(getAcceptedBets(computedBets)));
  }
  private placeBets(): void {
    const computedBets = this.computeBets();
    const totalBet = roundNumber(
      computedBets.reduce((sum, bet) => sum + bet.value, 0),
      this.store.getState().gameState.currencyInfo?.digitsNumber ?? 0
    );

    this.checkInactivity(totalBet === 0);
    this.updateAcceptedBets(computedBets);
  }
  private checkInactivity(inactiveRound = true): void {
    const state = this.store.getState();
    const inactiveRounds = state.gameState.inactivityCounter;
    if (inactiveRound) {
      const inactivityLimit = state.config.tableConfig?.idleLimit;
      if (inactivityLimit && inactiveRounds >= inactivityLimit) {
        this.store.dispatch(popupActions.pushPopup(PopupType.Inactivity));
        // Pause Game stream and game socket if inactivity limit is reached
        this.store.dispatch(gameStateActions.setGamePaused(true));
        this.store.dispatch(gameStateActions.resetCurrentGameStatus());
      } else {
        this.store.dispatch(gameStateActions.setInactivityCounter(inactiveRounds + 1));
      }
    } else if (inactiveRounds > 0) {
      this.store.dispatch(gameStateActions.setInactivityCounter(0));
    }
  }
}
